import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

// Google Icon Component
const GoogleIcon = ({ className }: { className: string }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className={className}
  >
    <path
      fill="currentColor"
      d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
    />
  </svg>
);

// Animated Background Component
const BackgroundPaths = () => (
  <svg className="background-svg" viewBox="0 0 800 600">
    {/* Primary flowing waves */}
    <path
      className="animated-path primary-wave"
      d="M-100,300 Q200,100 400,300 T900,300"
      style={{ animationDelay: '0s' }}
    />
    <path
      className="animated-path secondary-wave"
      d="M-50,250 Q150,50 350,250 T750,250"
      style={{ animationDelay: '1.5s' }}
    />
    <path
      className="animated-path tertiary-wave"
      d="M-150,350 Q250,150 500,350 T1050,350"
      style={{ animationDelay: '3s' }}
    />

    {/* Faster moving accent lines */}
    <path
      className="animated-path accent-line fast"
      d="M-80,180 Q120,20 280,180 T640,180"
      style={{ animationDelay: '0.5s' }}
    />
    <path
      className="animated-path accent-line fast"
      d="M-120,420 Q280,260 560,420 T1120,420"
      style={{ animationDelay: '2s' }}
    />

    {/* Subtle background flows */}
    <path
      className="animated-path background-flow"
      d="M-200,150 Q100,50 300,150 T800,150"
      style={{ animationDelay: '4s' }}
    />
    <path
      className="animated-path background-flow"
      d="M-180,450 Q320,350 600,450 T1180,450"
      style={{ animationDelay: '5.5s' }}
    />

    {/* Floating particles */}
    <circle className="floating-particle" cx="150" cy="200" r="2" style={{ animationDelay: '0s' }} />
    <circle className="floating-particle" cx="300" cy="350" r="1.5" style={{ animationDelay: '2s' }} />
    <circle className="floating-particle" cx="450" cy="150" r="1" style={{ animationDelay: '4s' }} />
    <circle className="floating-particle" cx="200" cy="400" r="2.5" style={{ animationDelay: '6s' }} />
  </svg>
);

const SignInPage: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWithGoogle, accessDeniedReason, clearAccessDeniedReason } = useAuth();

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      clearAccessDeniedReason?.();
      setLoading(true);
      loginWithGoogle();
    } catch (err) {
      setError('Failed to sign in with Google. Please try again.');
      console.error('Sign in error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-container-dark">
      {/* Left Side - Branding with animated background */}
      <div className="signin-branding-dark">
        <div className="background-animation">
          <BackgroundPaths />
        </div>
        <div className="branding-content-dark">
          <h1 className="brand-title-dark">
            <div className="brand-icon-dark">●</div>
            Executive Analytics
          </h1>
          <div className="brand-description-dark">
            Welcome to the Systango Invoices Dashboard and AI-powered Invoice Assistant — sign in to access powerful tools and smart Dashboard
          </div>
        </div>
      </div>

      {/* Right Side - Sign In Form */}
      <div className="signin-form-container-dark">
        <div className="signin-card-dark">
          <div className="card-header-dark">
            <h2 className="card-title-dark">Executive Analytics</h2>
            <p className="card-description-dark">
              C-Level Financial Insights & Performance Metrics
            </p>
            <p className="card-subdescription-dark">
              Only for Authorized Accounts 🔒
            </p>
          </div>

          {/* Access denied (email not in whitelist) */}
          {accessDeniedReason && (
            <div className="error-message-dark access-denied-dark">
              {accessDeniedReason}
            </div>
          )}
          {/* Error Message */}
          {error && (
            <div className="error-message-dark">
              {error}
            </div>
          )}

          <div className="card-content-dark">
            <div className="social-buttons-dark">
              <button
                onClick={handleGoogleSignIn}
                className="signin-button-dark google"
                disabled={loading}
              >
                {loading ? (
                  <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <GoogleIcon className="social-icon-dark" />
                )}
                {loading ? 'Signing in...' : 'Google'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .signin-container-dark {
          display: flex;
          min-height: 100vh;
          width: 100%;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          background: #0f0f23;
          color: #ffffff;
        }

        /* Left Side - Branding with animated background */
        .signin-branding-dark {
          display: none;
          background: #1a1a2e;
          position: relative;
          overflow: hidden;
        }

        .background-animation {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0.3;
        }

        .background-svg {
          width: 100%;
          height: 100%;
        }

        .animated-path {
          stroke: #ffffff;
          stroke-width: 1;
          fill: none;
          opacity: 0.15;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        /* Primary wave - main flowing animation */
        .primary-wave {
          stroke-width: 1.5;
          opacity: 0.25;
          animation: primaryFlow 12s ease-in-out infinite;
        }

        /* Secondary wave - counter flow */
        .secondary-wave {
          stroke-width: 1.2;
          opacity: 0.2;
          animation: secondaryFlow 15s ease-in-out infinite reverse;
        }

        /* Tertiary wave - slow deep flow */
        .tertiary-wave {
          stroke-width: 1;
          opacity: 0.15;
          animation: tertiaryFlow 18s ease-in-out infinite;
        }

        /* Fast accent lines */
        .accent-line {
          stroke-width: 0.8;
          opacity: 0.3;
          animation: accentFlow 8s ease-in-out infinite;
        }

        .accent-line.fast {
          animation: fastAccentFlow 6s ease-in-out infinite;
        }

        /* Background flows - subtle movement */
        .background-flow {
          stroke-width: 0.5;
          opacity: 0.1;
          animation: backgroundFlow 20s linear infinite;
        }

        /* Floating particles */
        .floating-particle {
          fill: rgba(255, 255, 255, 0.4);
          animation: particleFloat 10s ease-in-out infinite;
        }

        /* Animation keyframes */
        @keyframes primaryFlow {
          0%, 100% {
            opacity: 0.25;
            transform: translateY(0px) translateX(0px) scale(1);
          }
          25% {
            opacity: 0.35;
            transform: translateY(-15px) translateX(5px) scale(1.02);
          }
          50% {
            opacity: 0.4;
            transform: translateY(-8px) translateX(-3px) scale(1.05);
          }
          75% {
            opacity: 0.3;
            transform: translateY(12px) translateX(8px) scale(1.01);
          }
        }

        @keyframes secondaryFlow {
          0%, 100% {
            opacity: 0.2;
            transform: translateY(0px) translateX(0px) rotate(0deg);
          }
          33% {
            opacity: 0.3;
            transform: translateY(10px) translateX(-8px) rotate(0.5deg);
          }
          66% {
            opacity: 0.25;
            transform: translateY(-12px) translateX(6px) rotate(-0.3deg);
          }
        }

        @keyframes tertiaryFlow {
          0%, 100% {
            opacity: 0.15;
            transform: translateY(0px) scale(1);
          }
          40% {
            opacity: 0.25;
            transform: translateY(-20px) scale(1.03);
          }
          80% {
            opacity: 0.18;
            transform: translateY(8px) scale(0.98);
          }
        }

        @keyframes accentFlow {
          0%, 100% {
            opacity: 0.3;
            transform: translateY(0px) translateX(0px);
          }
          50% {
            opacity: 0.5;
            transform: translateY(-25px) translateX(10px);
          }
        }

        @keyframes fastAccentFlow {
          0%, 100% {
            opacity: 0.2;
            transform: translateY(0px) translateX(0px) scaleX(1);
          }
          25% {
            opacity: 0.4;
            transform: translateY(-18px) translateX(-5px) scaleX(1.1);
          }
          75% {
            opacity: 0.35;
            transform: translateY(15px) translateX(12px) scaleX(0.9);
          }
        }

        @keyframes backgroundFlow {
          0% {
            opacity: 0.05;
            transform: translateX(-50px);
          }
          50% {
            opacity: 0.15;
            transform: translateX(25px);
          }
          100% {
            opacity: 0.05;
            transform: translateX(-50px);
          }
        }

        @keyframes particleFloat {
          0%, 100% {
            opacity: 0.2;
            transform: translateY(0px) translateX(0px) scale(1);
          }
          25% {
            opacity: 0.6;
            transform: translateY(-30px) translateX(15px) scale(1.2);
          }
          50% {
            opacity: 0.4;
            transform: translateY(-10px) translateX(-8px) scale(0.8);
          }
          75% {
            opacity: 0.7;
            transform: translateY(20px) translateX(20px) scale(1.1);
          }
        }

        .branding-content-dark {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 100%;
          padding: 4rem;
          color: white;
          z-index: 2;
          position: relative;
        }

        .brand-title-dark {
          font-size: 1.25rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: auto;
          color: #ffffff;
        }

        .brand-icon-dark {
          width: 24px;
          height: 24px;
          background: #ffffff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: #1a1a2e;
          animation: smoothIconPulse 4s ease-in-out infinite;
          box-shadow: 0 0 8px rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        .brand-description-dark {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 2rem;
          animation: fadeIn 1s ease-in-out, subtlePulse 4s ease-in-out infinite;
          line-height: 1.5;
        }

        /* Right Side - Form */
        .signin-form-container-dark {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          background-color: #0f0f23;
        }

        .signin-card-dark {
          width: 100%;
          max-width: 24rem;
          background: transparent;
          animation: fadeIn 1s ease-in-out;
        }

        .card-header-dark {
          padding: 0 0 2rem 0;
          text-align: center;
        }

        .card-title-dark {
          font-size: 2rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .card-description-dark {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
          line-height: 1.4;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .card-subdescription-dark {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.875rem;
          line-height: 1.4;
        }

        .card-content-dark {
          padding: 0;
        }

        /* Error Message */
        .error-message-dark {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.2);
          color: #ef4444;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          margin-bottom: 1.5rem;
          text-align: center;
        }
        .access-denied-dark {
          background: rgba(245, 158, 11, 0.1);
          border-color: rgba(245, 158, 11, 0.35);
          color: #f59e0b;
        }

        /* Social Buttons Container */
        .social-buttons-dark {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        /* Button Styles */
        .signin-button-dark {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s;
          cursor: pointer;
          border: 1px solid #374151;
          background-color: transparent;
          color: #ffffff;
          width: 100%;
        }

        .signin-button-dark:hover:not(:disabled) {
          background-color: #1f2937;
          border-color: #4b5563;
        }

        .signin-button-dark:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .signin-button-dark.google {
          border-color: #374151;
        }

        /* Social Icons */
        .social-icon-dark {
          width: 1rem;
          height: 1rem;
          fill: currentColor;
        }

        .loading-spinner {
          width: 1rem;
          height: 1rem;
          animation: spin 1s linear infinite;
        }

        /* Additional Animations */
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes subtlePulse {
          0%, 100% {
            opacity: 0.7;
          }
          50% {
            opacity: 0.9;
          }
        }

        @keyframes smoothIconPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 8px rgba(255, 255, 255, 0.2);
            opacity: 0.9;
          }
          25% {
            transform: scale(1.02);
            box-shadow: 0 0 12px rgba(255, 255, 255, 0.3);
            opacity: 0.95;
          }
          50% {
            transform: scale(1.04);
            box-shadow: 0 0 16px rgba(255, 255, 255, 0.4);
            opacity: 1;
          }
          75% {
            transform: scale(1.02);
            box-shadow: 0 0 12px rgba(255, 255, 255, 0.3);
            opacity: 0.95;
          }
        }

        /* Responsive Design */
        @media (min-width: 1024px) {
          .signin-branding-dark {
            display: flex;
            width: 50%;
          }

          .signin-form-container-dark {
            width: 50%;
          }
        }

        /* Mobile adjustments */
        @media (max-width: 1023px) {
          .signin-form-container-dark {
            padding: 1rem;
          }

          .card-title-dark {
            font-size: 1.75rem;
          }

          .brand-description-dark {
            font-size: 0.9rem;
          }
        }
      ` }} />
    </div>
  );
};

export default SignInPage;
